import React, { useState, useEffect } from 'react';
import { fetchShotHistory } from '../services/api';
import {
  PF_TEXT, THERMAL_SPEC, buildInspectionSample, round2, judgeColor, judgeBg,
} from '../utils/inspectionSample';
import visionSampleImage from '../assets/vision-sample.png';
import thermalSampleImage from '../assets/thermal-sample.png';
import AIModalShell from './AIModalShell';
import './AIInspectionModal.css';

/* 비전 / 열화상 두 모달은 좌측(사출정보·Shot History)이 같고 우측 검사 영역만 다르다.
   type 으로 갈라 한 컴포넌트로 둔다. */
const TYPES = {
  vision: {
    title: 'AI 비전검사',
    icon: 'photo_camera',
    paneTitle: '비전검사',
    image: visionSampleImage,
    alt: '비전검사 예시',
  },
  thermal: {
    title: 'AI 열화상검사',
    icon: 'thermostat',
    paneTitle: '열화상검사',
    image: thermalSampleImage,
    alt: '열화상검사 예시',
  },
};

const fx = (v, d = 2) => (v === null || v === undefined ? '-' : Number(v).toFixed(d));

function AIInspectionModal({ type, open, onClose }) {
  const cfg = TYPES[type] || TYPES.vision;
  const [shots, setShots] = useState([]);
  const [selectedShot, setSelectedShot] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const h = await fetchShotHistory(null, 100);
        if (cancelled) return;
        const list = h?.data?.shots || [];
        setShots(list);
        setSelectedShot(list.length ? list[0].shotCount : null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const current = shots.find((s) => s.shotCount === selectedShot) || null;
  const sample = current ? buildInspectionSample(current.shotCount, current.qualityStatus) : null;
  const result = sample ? (type === 'thermal' ? sample.thermal : sample.vision) : null;
  const ok = result?.ok;

  return (
    <AIModalShell open={open} onClose={onClose} icon={cfg.icon} title={cfg.title}>
      {loading || !sample ? (
        <div className="aim-empty">검사 결과를 불러오는 중...</div>
      ) : (
        <div className="aiin-grid">
          {/* ── 사출정보 ── */}
          <section className="aim-pane">
            <h3 className="aim-title">사출정보</h3>
            <div className="aiin-product">
              <img src="/product-blueprint.png" alt="제품 이미지"
                   onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            </div>
            <table className="aiin-kv">
              <tbody>
                <tr><th>제품번호</th><td>{current?.craftId || '-'}</td></tr>
                <tr><th>선택 샷</th><td>#{current?.shotCount ?? '-'}</td></tr>
                <tr>
                  <th>공정 판정</th>
                  <td>
                    <span className={'aim-badge ' + (current?.qualityStatus === 'GOOD' ? 'good' : 'bad')}>
                      {current?.qualityStatus === 'GOOD' ? '양품' : '불량'}
                    </span>
                  </td>
                </tr>
                <tr>
                  <th>{cfg.paneTitle}</th>
                  <td>
                    <span className={'aim-badge ' + (ok ? 'good' : 'bad')}>
                      {ok ? PF_TEXT.good : PF_TEXT.defect}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </section>

          {/* ── Shot History ── */}
          <section className="aim-pane">
            <h3 className="aim-title">
              Shot History
              <span className="aim-title-note">샷을 고르면 검사 결과가 바뀝니다</span>
            </h3>
            <div className="aiin-shot-scroll">
              <table className="aiin-shot-table">
                <thead>
                  <tr><th>샷</th><th>계량위치</th><th>사출시간</th><th>공정</th><th>{cfg.paneTitle}</th></tr>
                </thead>
                <tbody>
                  {shots.map((s) => {
                    const smp = buildInspectionSample(s.shotCount, s.qualityStatus);
                    const r = type === 'thermal' ? smp.thermal : smp.vision;
                    return (
                      <tr
                        key={s.shotCount}
                        className={s.shotCount === selectedShot ? 'selected' : undefined}
                        onClick={() => setSelectedShot(s.shotCount)}
                      >
                        <td className="aiin-shot-num">#{s.shotCount}</td>
                        <td>{fx(s.chargePosi1, 1)}</td>
                        <td>{fx(s.injTime, 2)}</td>
                        <td>
                          <span className={'aim-badge ' + (s.qualityStatus === 'GOOD' ? 'good' : 'bad')}>
                            {s.qualityStatus === 'GOOD' ? '양' : '불'}
                          </span>
                        </td>
                        <td>
                          <span className={'aim-badge ' + (r.ok ? 'good' : 'bad')}>
                            {r.ok ? '양' : '불'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {/* ── 검사 영역 ── */}
          <section className="aim-pane aiin-inspect">
            <h3 className="aim-title">
              {cfg.paneTitle}
              <span className="aim-title-note">#{current?.shotCount ?? '-'}</span>
            </h3>

            <div className="aiin-image" style={{ borderColor: judgeColor(ok) }}>
              <img src={cfg.image} alt={cfg.alt} className="aiin-photo" />
              {!ok && type === 'vision' && (
                <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="aiin-overlay">
                  {result.blobs.map((b, i) => (
                    <g key={i}>
                      <circle cx={b.cx} cy={b.cy} r={b.r}
                              fill="rgba(239, 68, 68, 0.30)" stroke="#EF4444" strokeWidth="0.9" />
                      <circle cx={b.cx} cy={b.cy} r={b.r + 3.5}
                              fill="none" stroke="#EF4444" strokeWidth="0.6" strokeDasharray="2 2" />
                    </g>
                  ))}
                </svg>
              )}
              {!ok && type === 'thermal' && (
                <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="aiin-overlay">
                  <circle cx="46" cy="48" r="9" fill="none" stroke="#FFFFFF"
                          strokeWidth="1.1" strokeDasharray="3 2" />
                  <text x="46" y="33" fill="#FFFFFF" fontSize="7" fontWeight="600" textAnchor="middle">
                    {fx(result.tempMax, 1)}&#176;C
                  </text>
                </svg>
              )}
            </div>

            <div className="aiin-result">
              <table className="aim-table aiin-result-values">
                <thead>
                  <tr>
                    {type === 'vision' ? (
                      <>
                        <th>{PF_TEXT.confidence}</th>
                        <th>{PF_TEXT.finalThreshold}</th>
                        <th>{PF_TEXT.defectType}</th>
                      </>
                    ) : (
                      <>
                        <th>{PF_TEXT.tempMax}</th>
                        <th>{PF_TEXT.tempAvg}</th>
                        <th>{PF_TEXT.tempMin}</th>
                        <th>{PF_TEXT.tempSpec}</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    {type === 'vision' ? (
                      <>
                        <td style={{ color: judgeColor(ok) }}>{(result.score * 100).toFixed(1)}%</td>
                        <td className="aiin-muted">80.0%</td>
                        <td style={{ color: judgeColor(ok) }}>
                          {result.defectKey ? PF_TEXT[result.defectKey] : PF_TEXT.noDefect}
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={{ color: result.tempMax > THERMAL_SPEC.max ? '#EF4444' : '#10B981' }}>
                          {fx(result.tempMax, 1)}&#176;C
                        </td>
                        <td>{fx(result.tempAvg, 1)}&#176;C</td>
                        <td>{fx(result.tempMin, 1)}&#176;C</td>
                        <td className="aiin-muted">
                          {THERMAL_SPEC.min} ~ {THERMAL_SPEC.max}&#176;C
                        </td>
                      </>
                    )}
                  </tr>
                </tbody>
              </table>
              <table className="aim-table aiin-result-judge">
                <thead><tr><th>{PF_TEXT.passFailHeader}</th></tr></thead>
                <tbody>
                  <tr>
                    <td>
                      <span className="aiin-verdict"
                            style={{ background: judgeBg(ok), color: judgeColor(ok) }}>
                        {ok ? PF_TEXT.good : PF_TEXT.defect}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* 최근 30샷 판정 스트립 — 이 검사에서 불량이 어떻게 흩어져 있는지 */}
            <div className="aiin-strip">
              <div className="aiin-strip-head">
                <span>최근 30샷 {cfg.paneTitle} 판정</span>
                <span className="aiin-strip-note">클릭으로 이동</span>
              </div>
              <div className="aiin-strip-cells">
                {shots.slice(0, 30).slice().reverse().map((s) => {
                  const smp = buildInspectionSample(s.shotCount, s.qualityStatus);
                  const r = type === 'thermal' ? smp.thermal : smp.vision;
                  return (
                    <button
                      type="button"
                      key={s.shotCount}
                      title={'#' + s.shotCount + ' · ' + (r.ok ? '양품' : '불량')}
                      className={'aiin-cell' + (r.ok ? '' : ' bad')
                        + (s.shotCount === selectedShot ? ' selected' : '')}
                      onClick={() => setSelectedShot(s.shotCount)}
                    />
                  );
                })}
              </div>
            </div>
          </section>
        </div>
      )}
    </AIModalShell>
  );
}

export default AIInspectionModal;
